import {Component, Input, OnInit, OnDestroy, Output, EventEmitter} from '@angular/core';
import {Components, Positions, PositionsList} from "../../../../_shared/interfaces";
import {AlertService, ComponentsStoreService, IAlertType, ProjectsStoreService} from "../../../../_shared/services";
import {JsonPipe, NgForOf, NgIf, NgSwitch, NgSwitchCase} from "@angular/common";
import { RouterLink } from "@angular/router";
import {ClrCommonFormsModule, ClrDropdownModule, ClrIconModule, ClrTooltipModule} from "@clr/angular";
import {
  bundleIcon,
  checkCircleIcon,
  childArrowIcon,
  ClarityIcons,
  cloneIcon,
  cogIcon, formIcon,
  infoCircleIcon,
  nodeIcon, pencilIcon, timesIcon, trashIcon, floppyIcon
} from "@cds/core/icon";
import { EditPositionComponent } from "./edit-position/edit-position.component";

import { environment as env } from "../../../../../environments/environment";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
  AbstractControl,
  ReactiveFormsModule
} from "@angular/forms";
import {Subject, debounceTime, distinctUntilChanged, filter, switchMap} from "rxjs";
import {tap} from "rxjs/operators";

@Component({
  selector: 'app-project-details-positions',
  standalone: true,
  imports: [
    NgIf,
    NgSwitch,
    NgSwitchCase,
    RouterLink,
    ClrIconModule,
    ClrTooltipModule,
    NgForOf,
    EditPositionComponent,
    ClrDropdownModule,
    JsonPipe,
    ClrCommonFormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './project-details-positions.component.html',
  styleUrl: './project-details-positions.component.sass'
})
export class ProjectDetailsPositionsComponent implements OnInit, OnDestroy {
  public positionsForm: FormGroup = this.fb.group({});  // Initialize with empty form
  foundComponents: Components[] = [];
  positions: Positions[] = [];
  public projectNumber: string = '';
  public loading: boolean = true;
  public posLength: number = 0;
  public updateIndex = 999;
  public placeholderI: string = "...";
  public placeholderII: string = "...";
  devMode: boolean;

  // Search components
  keyUp$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  onKeyUp(event: KeyboardEvent): void {
    const inputElement = event.target as HTMLInputElement;
    this.keyUp$.next(inputElement.value);
  }

  @Input() cidPos: number = 0;
  @Input() pridPos: string = '';
  @Input() editPos: number = 0;  // 0 = Anzeige; 1 = Änderungsmodus, 2 = Position ändern
  @Input() editPositionNumber: number = 0;
  @Input() editPartNumber: number = 0;
  @Input() updPos: boolean = false;

  constructor(
    private prss: ProjectsStoreService,
    private compss: ComponentsStoreService,
    private alertDispatcher: AlertService,
    private fb: FormBuilder,
  ) {
    ClarityIcons.addIcons(infoCircleIcon, cogIcon, nodeIcon, checkCircleIcon, childArrowIcon, cloneIcon, bundleIcon, trashIcon, formIcon, pencilIcon, floppyIcon);
    this.devMode = env.stages.dev;
    this.initForm();  // Initialize form in constructor
  }

  ngOnInit() {
    this.projectNumber = this.cidPos + '.' + this.pridPos;
    this.loadProjectsPositions(this.projectNumber);
    this.initForm();

    this.keyUp$.pipe(
      filter(term => term.length >= 3),
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.loading = true),
      switchMap(searchTerm => this.compss.getAllSearch(searchTerm, 'all', 'all', true)),
      tap(() => this.loading = false)
    )
      .subscribe({
        next: (components) => {
          if (components?.object) {
            this.foundComponents = components.object;
            if (this.devMode) console.log('Found components:', this.foundComponents);
          }
        },
        error: (error) => {
          if (this.devMode) console.error(' Error searching components:', error);
          this.alertDispatcher.show({
            message: ' Fehler bei der Komponentensuche ' + error,
            clrAlertType: IAlertType.DANGER
        }, 2000);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  preFillRow(drawnumber: string, posName: string) {
    this.placeholderI = drawnumber;
    this.placeholderII = posName;
    this.positionsForm.patchValue({
      project_number: this.projectNumber,
      name: posName,
      technical_drawing: drawnumber
    })
  }

  initForm() {
    this.positionsForm = this.fb.group({
      budget: [0],
      comment_phm: [''],
      controlling_nr: [''],
      delivery_date: ['01.01.2020'], // new FormControl(this.defaultDate),
      description: [''],
      heat_nr: [''],
      material: [''],
      name: [''],
      number_of_pieces: ['', [Validators.required, Validators.pattern(/^\d+$/)]], // Required + Ganze Zahl
      position: [''],
      project_number: [this.projectNumber],
      serial_nr: [''],
      specification: null,
      status: [''],
      technical_drawing: ['', [Validators.required, this.notEllipsisValidator()]],
    });
  }

  /**
   * Custom Validator: Prüft, ob der Wert nicht '...' ist
   */
  notEllipsisValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value === '...' ? { invalidValue: 'Technical drawing darf nicht "..." sein' } : null;
    };
  }

  loadProjectsPositions(prid: string) {
    this.prss.getPositionsParts(prid, "0", "0").subscribe({
      next: data => {
        if (!data || !data.object || data.object.length === 0) {
          this.alertDispatcher.show({
            message: ' Keine Projektpositionen gefunden! ',
            clrAlertType: IAlertType.WARNING
          }, 2000);
          return;
        }
        this.positions = Object.values(data.object);
      },
      complete: () => {
        if (this.devMode) console.table(this.positions);
        this.posLength = this.positions.length;
        this.loading = false;
      },
      error: (err) => {
        this.alertDispatcher.show({
          message: ' Error: Projektpositionen konnten nicht geladen werden! ' + err,
          clrAlertType: IAlertType.DANGER
        }, 2000);
        return
      }
    });
  }

  updatePositionMode(index: number, pos: number, part: number) {
    this.updateIndex = index;
    this.editPos = 2;
    this.editPositionNumber = pos;
    this.editPartNumber = part;
    this.updPos = true;
  }

  addPosition() {
    this.prss.addPosition(this.positionsForm.value)
      .subscribe({
        next: () => {
          this.alertDispatcher.show({
            message: ' Neue Position wurde hinzugefügt! ',
            clrAlertType: IAlertType.SUCCESS
          }, 2000);
          this.loadProjectsPositions(this.projectNumber);
        },
        complete: () => {
          this.placeholderI = "...";
          this.placeholderII = "...";
          this.initForm()
        },
        error: (err) => {
          this.alertDispatcher.show({
            message: 'Error: Position konnten nicht hinzugefügt werden! ' + err,
            clrAlertType: IAlertType.DANGER
          }, 2000);
          return
        }
      });
  }

  updatePosition(done: boolean) {
    this.editPos = 1;
    this.updPos = false;
    this.updateIndex = 999;
    this.loadProjectsPositions(this.projectNumber);
  }

  deletePosition(positionId: number, partId: number) {
    switch(partId) {
      case 0: {
        if (confirm('Position ' + this.projectNumber + '.' + positionId + ' komplett (inkl. Einzelteile) löschen?')) {
          this.prss.deletePosition(this.projectNumber, positionId, partId)
            .subscribe({
              next: () => {
                this.alertDispatcher.show({
                  message: 'Projektposition ' + positionId + '.' + partId + ' wurde gelöscht! ',
                  clrAlertType: IAlertType.INFO
                }, 2000);
              },
              complete: () => {
                this.loadProjectsPositions(this.projectNumber);
                },
              error: (err) => {
                this.alertDispatcher.show({
                  message: 'Error: Projektposition konnten nicht gelöscht werden! ' + err,
                  clrAlertType: IAlertType.DANGER
                }, 2000);
                return
              }
            });
        }
        break;
      }
      default: {
        if (confirm('Position ' + this.projectNumber + '.' + positionId + ' Teil ' + partId + ' wirklich löschen?')) {
          this.prss.deletePosition(this.projectNumber, positionId, partId)
            .subscribe({
              next: () => {
                this.loadProjectsPositions(this.projectNumber);
              },
              complete: () => {},
              error: () => {
                return
              }
            });
        }
        break;
      }
    }
  }

  applyPartsList(technicalDrawing: string, projectId: string, positionId: number, multiplicator: number) {
    if (confirm('Wollen Sie die Teileliste für die Komponente ' + technicalDrawing + ' in das Projekt ' + projectId + ' (Position ' + positionId + ') übernehmen?')) {
      this.prss.addPositionParts(projectId, positionId.toString(), technicalDrawing, multiplicator)
        .subscribe({
          next: () => {
            this.loadProjectsPositions(projectId);
          },
          complete: () => {},
          error: () => {
            return
          }
        });
    }
  }

  warnPassiv() {
    this.alertDispatcher.show({
      message: ' ACHTUNG! Passive Komponente(n)! ',
      clrAlertType: IAlertType.WARNING
    });
    return
  }

  private requiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return { required: true };
      }
      return null;
    };
  }
}
